import { takeUntil, filter } from 'rxjs/operators';
import { CompositeCommand, type Command, type History } from '@o2d/history';

import type { WhiteboardBus } from '../../core/bus/whiteboard-bus.js';
import type { UiAction } from '../../core/bus/ui-action.events.js';
import { objectId } from '../../core/bus/branded-types.js';
import type { BoardState } from '../../core/state/board-state.js';
import type { ObjectRegistry } from '../../core/object-registry.js';
import { AddObjectCommand } from '../../core/command/add-object.command.js';
import { findSnap, resolveAnchor, anchorBoundsOf, type AnchorTarget } from '../../objects/line-anchor.js';
import { SNAP_SCREEN_PX } from '../../constants/whiteboard-config.constants.js';
import { createLineDefaults } from './line-defaults.factory.js';
import { SwapLineCapsCommand, SetLinePathStrategyCommand, type SwapSnapshot } from './line.commands.js';
import { isLine } from './line.types.js';
import type { LineAnchor, LineCap } from './line.interface.js';
import type { Point } from '../../core/bus/ui-action.events.js';
import type { BoardObject } from '../../core/interfaces/board-object.interface.js';

/**
 * Subscribes to two line-related UiActions and bundles the line-slice
 * lifecycle in a single controller (PRD-F5 TR12):
 *
 *  1. `place/line` — snaps both endpoints via `findSnap`, builds the line
 *     payload through `createLineDefaults`, pushes an `AddObjectCommand`
 *     onto history, then re-emits `selection/set` for the new id.
 *  2. `line/swap-caps` — reads each line from `BoardState`, builds a
 *     `SwapSnapshot` of the swap-relevant pre-image fields, and pushes a
 *     `SwapLineCapsCommand` per line so undo restores the prior state.
 *  3. `line/set-path` — a `SetLinePathStrategyCommand` per line whose strategy
 *     actually changes.
 *
 * 2 and 3 take ALL selected lines (`ids`) and push them as ONE undo step — a
 * `CompositeCommand` when more than one line changes (Anke, 2026-10-03).
 *
 * Lifecycle: both subscriptions are bound to `bus.destroy$` via `takeUntil`.
 * The controller is NOT a Lit ReactiveController — its lifetime equals
 * the bus (mirrors the StickyPlacementController pattern).
 */
export class LineLifecycleController {
    readonly #bus: WhiteboardBus;
    readonly #state: BoardState;
    readonly #history: History<BoardState>;
    readonly #registry: ObjectRegistry;

    constructor(
        bus: WhiteboardBus,
        state: BoardState,
        history: History<BoardState>,
        registry: ObjectRegistry,
    ) {
        this.#bus = bus;
        this.#state = state;
        this.#history = history;
        this.#registry = registry;

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'place/line' }> =>
                        a.type === 'place/line',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handlePlace(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[LineLifecycleController] place/line bus error swallowed');
                    }
                },
            });

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'line/swap-caps' }> =>
                        a.type === 'line/swap-caps',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handleSwapCaps(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[LineLifecycleController] line/swap-caps bus error swallowed');
                    }
                },
            });

        this.#bus.uiActions$
            .pipe(
                filter(
                    (a): a is Extract<UiAction, { type: 'line/set-path' }> =>
                        a.type === 'line/set-path',
                ),
                takeUntil(this.#bus.destroy$),
            )
            .subscribe({
                next: (action) => this.#handleSetPath(action),
                error: () => {
                    if (import.meta.env?.DEV) {
                        console.warn('[LineLifecycleController] line/set-path bus error swallowed');
                    }
                },
            });
    }

    /** Snap a world-space point to a nearby anchor target, if any. */
    #snap(worldX: number, worldY: number) {
        const scale = this.#bus.viewport$.value.scale || 1;
        const worldRadius = SNAP_SCREEN_PX / scale;
        return findSnap(
            this.#state.allObjects(),
            this.#registry,
            worldX,
            worldY,
            worldRadius,
        );
    }

    #handlePlace(action: Extract<UiAction, { type: 'place/line' }>): void {
        // Prefer the preview-captured anchors (`pendingStartAnchor` /
        // `pendingEndAnchor`) over a fresh `findSnap` so the committed line
        // matches exactly what the user saw in the preview overlay (PRD-F5
        // §T19). Fall back to a controller-local snap when the caller did
        // not pre-snap (e.g. tests that dispatch `place/line` directly).
        const startResolved = this.#resolvePending(action.pendingStartAnchor)
            ?? this.#snap(action.from.x, action.from.y);
        const endResolved = this.#resolvePending(action.pendingEndAnchor)
            ?? this.#snap(action.to.x, action.to.y);

        const from: Point = startResolved ? { x: startResolved.x, y: startResolved.y } : action.from;
        const to: Point = endResolved ? { x: endResolved.x, y: endResolved.y } : action.to;

        const scale = this.#bus.viewport$.value.scale || 1;
        // Stefan, 2026-09-03 — "choose properties before drawing", see the
        // identical comment in rectangle-placement.controller.ts.
        //
        // Bugreport (Anke, 2026-09-03): "funktioniert vorauswahl nicht für:
        // linienenden, linienform, linienart (gestrichelt etc.)" — start/end
        // cap, path strategy and stroke style were originally excluded from
        // staging entirely; all three resolve to fields
        // `createLineDefaults`'s options already accept (`startCap`/`endCap`
        // already had the plumbing, unused; `strokeStyle` is new). Only
        // `line-swap-caps` (a distinct "swap the two existing ends" command —
        // nothing to swap before the line exists) stays out of scope.
        //
        // Review-Lauf 01 (2026-09-03): `boxShadow` staged correctly (the
        // options circle's shadow toggle has a plain `resolveProperty`
        // mapping) but was never read here — same bugreport class as the
        // rest of this list, just missed initially.
        const staged = this.#bus.stagedToolProperties$.value.line;
        const line = createLineDefaults(scale, from, to, {
            ...(startResolved ? { startAnchor: startResolved.anchor } : {}),
            ...(endResolved ? { endAnchor: endResolved.anchor } : {}),
            ...(staged?.['strokeColor'] !== undefined ? { strokeColor: staged['strokeColor'] as string } : {}),
            ...(staged?.['strokeWidth'] !== undefined ? { strokeWidth: staged['strokeWidth'] as number } : {}),
            ...(staged?.['strokeStyle'] !== undefined ? { strokeStyle: staged['strokeStyle'] as 'solid' | 'dashed' | 'dotted' } : {}),
            ...(staged?.['startCap'] !== undefined ? { startCap: staged['startCap'] as LineCap } : {}),
            ...(staged?.['endCap'] !== undefined ? { endCap: staged['endCap'] as LineCap } : {}),
            ...(staged?.['pathStrategy'] !== undefined ? { pathStrategy: staged['pathStrategy'] as string } : {}),
            ...(staged?.['boxShadow'] !== undefined ? { boxShadow: staged['boxShadow'] as 'on' } : {}),
        });

        this.#history.push(new AddObjectCommand(line), this.#state);

        const newId = objectId(line.id);
        this.#bus.uiActions$.next({ type: 'selection/set', ids: [newId] });
    }

    /**
     * Resolve a preview-captured `LineAnchor` to its current world point.
     * Returns `null` when the target object is gone or not an anchor target,
     * so the caller can fall back to a fresh `findSnap`.
     */
    #resolvePending(anchor: LineAnchor | undefined): { x: number; y: number; anchor: LineAnchor } | null {
        if (!anchor) return null;
        const target = this.#state.getObject(anchor.objectId) as BoardObject | undefined;
        if (!target) return null;
        const plugin = this.#registry.get(target.type);
        if (!plugin?.isAnchorTarget?.()) return null;
        const { x, y } = resolveAnchor(anchor, anchorBoundsOf(target as AnchorTarget, this.#registry));
        return { x, y, anchor };
    }

    #handleSwapCaps(action: Extract<UiAction, { type: 'line/swap-caps' }>): void {
        const commands: Command<BoardState>[] = [];
        for (const id of action.ids) {
            const obj = this.#state.getObject(id);
            if (!obj || !isLine(obj)) continue;
            const before: SwapSnapshot = {
                x1: obj.x1,
                y1: obj.y1,
                x2: obj.x2,
                y2: obj.y2,
                startCap: obj.startCap,
                endCap: obj.endCap,
                ...(obj.startAnchor !== undefined ? { startAnchor: obj.startAnchor } : {}),
                ...(obj.endAnchor !== undefined ? { endAnchor: obj.endAnchor } : {}),
            };
            commands.push(new SwapLineCapsCommand(id, before));
        }
        this.#pushAsOneStep(commands, 'Linien-Enden tauschen');
    }

    #handleSetPath(action: Extract<UiAction, { type: 'line/set-path' }>): void {
        const commands: Command<BoardState>[] = [];
        for (const id of action.ids) {
            const obj = this.#state.getObject(id);
            if (!obj || !isLine(obj)) continue;
            if (obj.pathStrategy === action.strategy) continue; // already there — no empty undo step
            commands.push(new SetLinePathStrategyCommand(id, obj.pathStrategy, action.strategy));
        }
        this.#pushAsOneStep(commands, 'Linienform');
    }

    /** Push `commands` as ONE undo step: nothing for none, the command itself
     *  for one, a `CompositeCommand` for several (as `updateSelectedProperty`). */
    #pushAsOneStep(commands: Command<BoardState>[], label: string): void {
        if (commands.length === 0) return;
        this.#history.push(
            commands.length === 1
                ? commands[0]!
                : new CompositeCommand(commands, `${commands.length} × ${label}`),
            this.#state,
        );
    }
}
